import { RemoteEndpointsModel, RemoteServicesModel } from '@libs/shared/models';

/**
 * CMS
 */
export const CmsServiceConfig: RemoteEndpointsModel<'cms'> = {
  // API v2
  configuration: {
    method: 'get',
    path: '/cms/configuration',
  },
  content: {
    method: 'get',
    path: '/cms/content',
  },
  form: {
    method: 'post',
    path: '/cms/form/:formId',
  },

  // API v1
  page: {
    method: 'get',
    path: '/cms/page/:sectionId/:contentId',
  },
  structure: {
    method: 'get',
    path: '/cms/structure/:contentId',
  },
};

/**
 * Customer
 */
const CustomerServiceConfig: RemoteEndpointsModel<'customer'> = {
  customers: {
    method: 'get',
    path: '/customers',
  },
  customerByNumber: {
    method: 'get',
    path: '/customers/:customerNumber',
  },
  customerUpdate: {
    method: 'post',
    path: '/customers/:customerNumber',
  },
};

/**
 * Employee
 */
const EmployeeServiceConfig: RemoteEndpointsModel<'employee'> = {
  delegatableRoles: {
    method: 'get',
    path: '/employees/delegatable-roles',
  },
  customerEmployees: {
    method: 'get',
    path: '/employees',
  },
  createCustomerEmployee: {
    method: 'post',
    path: '/employees',
  },
  employees: {
    method: 'get',
    path: '/employees/:id',
  },
  deleteEmployee: {
    method: 'delete',
    path: '/employees/:id',
  },
  replaceEmployeeRoles: {
    method: 'put',
    path: '/employees/:id/roles',
  },
  renewInvitationEmployee: {
    method: 'post',
    path: '/employees/:id/renew-invitation',
  },
};

/**
 * Navigation
 */
const NavigationServiceConfig: RemoteEndpointsModel<'navigation'> = {
  navigation: {
    method: 'get',
    path: '/navigation',
  },
};

/**
 * Microservices
 */
const ServiceConfig: RemoteEndpointsModel<'service'> = {
  documents: {
    method: 'get',
    path: '/documents',
  },
  documentCounts: {
    method: 'get',
    path: '/documents/count',
  },
  documentFile: {
    method: 'get',
    path: '/documents/file/:fileIdentifier',
  },
  orderHistory: {
    method: 'get',
    path: '/order-history',
  },
  orderHistoryRecord: {
    method: 'get',
    path: '/order-history/:orderId',
  },
  orderDocuments: {
    method: 'get',
    path: '/order-history/:orderId/documents',
  },
  orderDocumentFile: {
    method: 'get',
    path: '/order-history/documents/:fileName',
  },
  orderStatus: {
    method: 'get',
    path: '/orders/:orderId/status',
  },
  deliveryWindows: {
    method: 'get',
    path: '/delivery-info/delivery-windows',
  },
  srmCategoryTree: {
    method: 'get',
    path: '/srm/category-tree',
  },
  srmForm: {
    method: 'get',
    path: '/srm/form/:formIdentifier',
  },
  srmTicketCreate: {
    method: 'post',
    path: '/srm/tickets',
  },
  srmTicket: {
    method: 'get',
    path: '/srm/tickets/:ticketIdentifier',
  },
  srmTickets: {
    method: 'get',
    path: '/srm/tickets',
  },
  srmFileUpload: {
    method: 'post',
    path: '/srm/file',
  },
  srmFile: {
    method: 'get',
    path: '/srm/file/:fileIdentifier',
  },
  srmTicketSuggestion: {
    method: 'post',
    path: '/srm/ticket-suggestion',
  },
  srmTicketSuggestionFile: {
    method: 'get',
    path: '/srm/ticket-suggestion/file/:fileIdentifier',
  },
  lmiv: {
    method: 'get',
    path: '/lmiv/:productNumber',
  },
};

/**
 * Shop
 */
const ShopServiceConfig: RemoteEndpointsModel<'shop'> = {
  catalogSearch: {
    method: 'get',
    path: '/shop/catalog-search',
  },
  catalogSearchSuggestions: {
    method: 'get',
    path: '/shop/catalog-search-suggestions/:q',
  },
  configurableBundleTemplateGetById: {
    method: 'get',
    path: '/shop/configurable-bundle-templates/:templateId',
  },
  configurableBundleAddToCart: {
    method: 'post',
    path: '/shop/cart/:cartId/configurable-bundles',
  },
  productBundles: {
    method: 'get',
    path: '/shop/product-bundles',
  },
  productDetail: {
    method: 'get',
    path: '/shop/product/:sku',
  },
  crossSelling: {
    method: 'get',
    path: '/shop/cross-selling',
  },
  lastOrderedProducts: {
    method: 'get',
    path: '/shop/last-ordered-products',
  },
  shoppingLists: {
    method: 'get',
    path: '/shop/shopping-lists',
  },
  shoppingList: {
    method: 'get',
    path: '/shop/shopping-lists/:listId',
  },
  shoppingListDownload: {
    method: 'get',
    path: '/shop/shopping-lists/:listId/download',
  },
  shoppingListsDelete: {
    method: 'delete',
    path: '/shop/shopping-lists/:listId',
  },
  shoppingListsCreate: {
    method: 'post',
    path: '/shop/shopping-lists',
  },
  shoppingListsUpdate: {
    method: 'patch',
    path: '/shop/shopping-lists/:listId',
  },
  shoppingListsAddProduct: {
    method: 'post',
    path: '/shop/shopping-lists/:listId/shopping-list-items',
  },
  shoppingListsAddProductCollection: {
    method: 'post',
    path: '/shop/shopping-lists/:listId/shopping-list-items-collection',
  },
  shoppingListsUpdateProduct: {
    method: 'patch',
    path: '/shop/shopping-lists/:listId/shopping-list-items/:itemId',
  },
  shoppingListsDeleteProduct: {
    method: 'delete',
    path: '/shop/shopping-lists/:listId/shopping-list-items/:itemId',
  },
  shoppingListsDeleteProductsCollection: {
    method: 'delete',
    path: '/shop/shopping-lists/:listId/shopping-list-items-collection',
  },
  cartGetActiveCartId: {
    method: 'get',
    path: '/shop/cart/active-cart-id',
  },
  cartGet: {
    method: 'get',
    path: '/shop/cart/:cartId',
  },
  cartDeleteAllItems: {
    method: 'delete',
    path: '/shop/cart/:cartId',
  },
  cartItemAdd: {
    method: 'post',
    path: '/shop/cart/:cartId/items',
  },
  cartItemsAddCollection: {
    method: 'post',
    path: '/shop/cart/:cartId/items-collection',
  },
  cartItemUpdate: {
    method: 'patch',
    path: '/shop/cart/:cartId/items/:itemId',
  },
  cartItemDelete: {
    method: 'delete',
    path: '/shop/cart/:cartId/items/:itemId',
  },
  cartItemsDeleteCollection: {
    method: 'delete',
    path: '/shop/cart/:cartId/items-collection',
  },
  addVoucherToCart: {
    method: 'post',
    path: '/shop/cart/:cartId/voucher',
  },
  removeVoucherFromCart: {
    method: 'delete',
    path: '/shop/carts/:cartId/vouchers/:voucherCode',
  },
  orderToken: {
    method: 'post',
    path: '/shop/order-token',
  },
  orderCheckout: {
    method: 'post',
    path: '/shop/order',
  },
  skuSearch: {
    method: 'get',
    path: '/shop/sku-search',
  },
  findProductsBySkus: {
    method: 'get',
    path: '/shop/find-products-by-skus',
  },
};

const AgentServiceConfig: RemoteEndpointsModel<'agent'> = {
  search: {
    method: 'get',
    path: '/agent/search',
  },
};

/**
 * User
 */
const UserServiceConfig: RemoteEndpointsModel<'user'> = {
  selfConfig: {
    method: 'get',
    path: '/user/config',
  },
  user: {
    method: 'get',
    path: '/user',
  },
  userUpdate: {
    method: 'post',
    path: '/user',
  },
  userDelete: {
    method: 'delete',
    path: '/user',
  },
  tosCheck: {
    method: 'get',
    path: '/user/tos/check',
  },
  tosCurrent: {
    method: 'get',
    path: '/user/tos/current',
  },
  tosConsent: {
    method: 'post',
    path: '/user/tos/consent',
  },
};

/**
 * Backend services
 */
export const RemoteServices: RemoteServicesModel = {
  agent: AgentServiceConfig,
  cms: CmsServiceConfig,
  customer: CustomerServiceConfig,
  employee: EmployeeServiceConfig,
  user: UserServiceConfig,
  service: ServiceConfig,
  shop: ShopServiceConfig,
  navigation: NavigationServiceConfig,
};
